/**
 * useVoiceSession — Client-Pipeline für das Voice-Brain-Panel (#1385, V1b).
 *
 * Spricht das HAUSEIGENE Event-Protokoll des V1a-Gateways /ws/voice (#1384):
 *   Client → Server: speech.started {mime} · Binär-Audio · speech.stopped
 *                    · abort (#1388 Barge-in) · session.end
 *   Server → Client: session.started {limits} · thinking · response.done {transcript,text}
 *                    · audio {format,encoding,data} · visual.show {visual} · visual.hide (#1399)
 *                    · session.ended {reason} · error {code}
 *
 * [PORTABEL]-Muster (Dynaflow-Voice-Agent, adaptiert auf WeShip-Gateway):
 *   - Mic-Capture: getUserMedia (echoCancellation/noiseSuppression/autoGainControl)
 *     + AudioWorklet → Float32-PCM → bei Utterance-Ende WAV 16 kHz mono → Binär-Frames.
 *   - VAD: RMS-basiert mit Pre-Roll-Ringpuffer (Anfang wird nicht abgeschnitten),
 *     Start nach ~150 ms Sprache, Stop nach ~500 ms Stille (#1388 F3).
 *   - Playback-Queue: decodeAudioData → AudioBufferSource → Analyser (Output-Amplitude).
 *   - Barge-in (#1388): Sprache während speaking/thinking → abort ans Gateway,
 *     Queue leeren + Source stoppen, neue Utterance startet sofort.
 *   - Echo-Guard (#1388 F2): dämpft nur das GEPUFFERTE Audio während die AI
 *     spricht; die VAD entscheidet auf dem ungedämpften Signal (eigene
 *     Barge-in-Schwelle 0.03/250 ms gegen Selbst-Trigger).
 *   - Trigger-Zähler mit Hangover (#1398): kurze Unter-Schwelle-Dips (≤80 ms)
 *     nullen die akkumulierte Sprechzeit nicht — Root-Cause-Fix aus #1397.
 *   - Audio während Run (#1404, Root-Cause #1402): Das Gateway sendet bei
 *     Tool-Läufen Präambel-Audio VOR response.done. Endet dieses Playback bei
 *     leerer Queue, fällt der State NICHT auf 'listening' zurück, solange der
 *     Server-Run läuft (runActiveRef) — sondern auf 'thinking'. Verhindert
 *     Geister-Capture (volle VAD-Sensitivität bei laufendem Run → Server-busy
 *     → verschlucktes speech.stopped → thinking-Deadlock). Präambel-Playback
 *     verhält sich damit exakt wie normales Speaking (Echo-Guard + Barge-in).
 *
 * Read-only (V1b): keine Action-/Write-UI. Fehler-Codes werden client-seitig
 * lokalisiert — keine Provider-/Modellnamen (AK6).
 *
 * @since v2.641.0 (#1385)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ── VAD/Audio-Tuning ──
const TARGET_SAMPLE_RATE = 16000;     // WAV an STT (klein + ausreichend für Speech)
const VAD_START_RMS = 0.022;          // Schwelle Sprach-Beginn
const VAD_STOP_RMS = 0.012;           // Schwelle Stille (Hysterese)
const VAD_START_MS = 150;             // so lange über Schwelle → Utterance beginnt
const VAD_STOP_MS = 400;              // so lange Stille → Utterance endet (#1388 F3: 900→500, #1403 H4: 500→400)
const PRE_ROLL_MS = 350;              // Ringpuffer vor Sprach-Beginn
const MAX_UTTERANCE_MS = 60000;       // Hard-Stop je Äußerung (Client-seitig)
const ECHO_GUARD_FACTOR = 0.15;       // Dämpfung NUR fürs gepufferte Audio während AI spricht
// #1393: 0.05 verlangte noch deutlich erhobene Stimme. 0.03 = knapp über der
// normalen Sprech-Schwelle (VAD_START 0.022); gegen Fehl-Trigger durch
// AI-Eigenaudio/Transienten gilt während speaking die LÄNGERE Bestätigungszeit
// BARGE_IN_MS statt VAD_START_MS (echoCancellation dämpft das Eigenaudio zusätzlich).
const BARGE_IN_RMS = 0.03;            // UNGEDÄMPFTE Schwelle während speaking (#1388 F2, #1393 gesenkt)
const BARGE_IN_MS = 250;              // Summe Sprechzeit über Schwelle während speaking → Barge-in (#1393)
// #1398 (Root-Cause #1397): Der Trigger-Zähler nullte bei JEDEM einzelnen
// 2,67-ms-Frame unter der Schwelle — natürliche Sprache ist amplitudenmoduliert
// (längste lückenlose Strecke gemessen 96–165 ms), die 250-ms-Bedingung war
// strukturell unerfüllbar (0/5 Äußerungen). Hangover: kurze Dips bis
// VAD_HANGOVER_MS erhalten die akkumulierte Sprechzeit (zählen selbst NICHT
// als Sprechzeit), erst längere zusammenhängende Stille setzt zurück.
// Gemessen #1397: 80 ms → 4/5 Trigger; Fehl-Trigger-Schutz bleibt, weil
// weiterhin BARGE_IN_MS SUMME echter Über-Schwelle-Zeit gefordert ist.
const VAD_HANGOVER_MS = 80;           // tolerierte Unter-Schwelle-Lücke im Trigger-Zähler (#1398)
const WS_BINARY_CHUNK = 512 * 1024;   // WAV in ≤512-KB-Frames senden

const WORKLET_CODE = `
class WeshipVoiceCapture extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) this.port.postMessage(new Float32Array(ch));
    return true;
  }
}
registerProcessor('weship-voice-capture', WeshipVoiceCapture);
`;

/** Float32 (sourceRate) → Int16-WAV (TARGET_SAMPLE_RATE, mono). */
function encodeWav(float32, sourceRate) {
  let samples = float32;
  if (sourceRate !== TARGET_SAMPLE_RATE) {
    const ratio = sourceRate / TARGET_SAMPLE_RATE;
    const outLen = Math.floor(float32.length / ratio);
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const pos = i * ratio;
      const i0 = Math.floor(pos);
      const i1 = Math.min(i0 + 1, float32.length - 1);
      out[i] = float32[i0] + (float32[i1] - float32[i0]) * (pos - i0);
    }
    samples = out;
  }
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);                       // PCM
  view.setUint16(22, 1, true);                       // mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, TARGET_SAMPLE_RATE * 2, true);  // byte rate
  view.setUint16(32, 2, true);                       // block align
  view.setUint16(34, 16, true);                      // bits
  writeStr(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return buffer;
}

function base64ToArrayBuffer(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/**
 * @returns {{
 *   state: 'idle'|'connecting'|'listening'|'thinking'|'speaking',
 *   amplitude: number, elapsed: number, maxSeconds: number|null,
 *   turns: Array<{role:'user'|'assistant', text:string}>,
 *   error: {code:string}|null, endReason: string|null,
 *   start: Function, stop: Function,
 * }}
 */
export function useVoiceSession() {
  const [state, setState] = useState('idle');
  const [amplitude, setAmplitude] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(null);
  const [turns, setTurns] = useState([]);
  const [error, setError] = useState(null);
  const [endReason, setEndReason] = useState(null);
  // #1387 — Action-Flow: vorgeschlagener Telegram-Versand wartet auf gesprochenes Ja.
  const [pendingAction, setPendingAction] = useState(null); // {recipient, message}
  const [actionResult, setActionResult] = useState(null);   // {status: sent|cancelled|timeout|dry_run|failed}
  // #1399 Voice-Visuals: eingeblendetes Visual {typ, meta, data} | null.
  // Schließt bei visual.hide ODER neuer Frage (speech.started, AK3).
  const [visual, setVisual] = useState(null);

  const stateRef = useRef('idle');
  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const workletRef = useRef(null);
  const analyserRef = useRef(null);     // Output-Amplitude (speaking)
  const sourceRef = useRef(null);       // aktive Playback-Source
  const queueRef = useRef([]);          // Playback-Queue (base64)
  const micRmsRef = useRef(0);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const vadRef = useRef({ capturing: false, speechMs: 0, gapMs: 0, silenceMs: 0, utterMs: 0 });
  const preRollRef = useRef([]);        // [{data:Float32Array}]
  const chunksRef = useRef([]);         // Utterance-PCM
  // #1393 (Bug B1, Befund #1395): decodeAudioData ist async — ohne synchrones
  // Busy-Flag passieren zwei kurz aufeinanderfolgende audio-Events beide den
  // sourceRef-Null-Guard und starten ÜBERLAPPENDE Sources (Doppelstimme).
  const decodingRef = useRef(false);    // genau EIN Decode in flight
  const playGenRef = useRef(0);         // Generation: invalidiert in-flight Decodes nach stopPlayback
  // #1404: Server-Run aktiv (speech.stopped gesendet, response.done/terminaler
  // Fehler noch nicht da) — Playback-Ende darf dann NICHT auf 'listening' fallen.
  const runActiveRef = useRef(false);
  // #1390 Standby-Modus (Kiosk): Idle-Timeout-Überwachung + Drain-Phase.
  // drainingRef: session.ended kam, aber die Verabschiedungs-Audio-Queue spielt
  // noch — Mic/WS sind schon zu, der volle Cleanup folgt nach dem letzten Chunk.
  const standbyOptsRef = useRef(null);  // { idleTimeoutSeconds } | null
  const lastActivityRef = useRef(0);
  const timeoutSentRef = useRef(false);
  const drainingRef = useRef(false);
  const cleanupRef = useRef(() => {}); // playNext (oben definiert) → cleanup (unten definiert)

  const setStateBoth = useCallback((s) => {
    stateRef.current = s;
    setState(s);
    lastActivityRef.current = Date.now(); // #1390: jeder Zustandswechsel = Aktivität
  }, []);

  // ── Playback (Queue + Barge-in) ──
  const stopPlayback = useCallback(() => {
    playGenRef.current++;               // in-flight Decode darf nach Abbruch nicht nachstarten
    decodingRef.current = false;
    queueRef.current = [];
    if (sourceRef.current) {
      try { sourceRef.current.onended = null; sourceRef.current.stop(); } catch { /* noop */ }
      sourceRef.current = null;
    }
  }, []);

  const playNext = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || sourceRef.current || decodingRef.current || !queueRef.current.length) return;
    decodingRef.current = true;         // SYNCHRON vor dem async Decode (B1)
    const gen = playGenRef.current;
    const b64 = queueRef.current.shift();
    ctx.decodeAudioData(base64ToArrayBuffer(b64)).then((audioBuf) => {
      decodingRef.current = false;
      if (gen !== playGenRef.current) return; // stopPlayback kam dazwischen
      if (!ctxRef.current || stateRef.current === 'idle') return;
      const src = ctx.createBufferSource();
      src.buffer = audioBuf;
      if (!analyserRef.current) {
        analyserRef.current = ctx.createAnalyser();
        analyserRef.current.fftSize = 512;
        analyserRef.current.connect(ctx.destination);
      }
      src.connect(analyserRef.current);
      src.onended = () => {
        sourceRef.current = null;
        if (queueRef.current.length) { playNext(); return; }
        // #1390: Drain-Phase — Verabschiedung fertig gespielt → jetzt komplett aufräumen.
        if (drainingRef.current) { cleanupRef.current(); return; }
        // #1404: Queue leer, aber Run läuft noch (Präambel vor response.done)
        // → zurück in 'thinking', NIE 'listening' (Geister-Capture-Schutz).
        if (stateRef.current === 'speaking') setStateBoth(runActiveRef.current ? 'thinking' : 'listening');
      };
      sourceRef.current = src;
      setStateBoth('speaking');
      src.start();
    }).catch(() => {
      decodingRef.current = false;
      if (gen !== playGenRef.current) return;
      sourceRef.current = null;
      if (queueRef.current.length) playNext();
      else if (drainingRef.current) cleanupRef.current(); // #1390 Drain auch im Fehlerfall beenden
      else if (stateRef.current === 'speaking') setStateBoth(runActiveRef.current ? 'thinking' : 'listening');
    });
  }, [setStateBoth]);

  // ── Utterance-Versand ──
  const flushUtterance = useCallback(() => {
    const ws = wsRef.current;
    const ctx = ctxRef.current;
    const chunks = chunksRef.current;
    chunksRef.current = [];
    if (!ws || ws.readyState !== 1 || !ctx || !chunks.length) return;
    let total = 0;
    for (const c of chunks) total += c.length;
    const pcm = new Float32Array(total);
    let off = 0;
    for (const c of chunks) { pcm.set(c, off); off += c.length; }
    const wav = encodeWav(pcm, ctx.sampleRate);
    for (let i = 0; i < wav.byteLength; i += WS_BINARY_CHUNK) {
      ws.send(wav.slice(i, Math.min(i + WS_BINARY_CHUNK, wav.byteLength)));
    }
    ws.send(JSON.stringify({ type: 'speech.stopped' }));
    runActiveRef.current = true; // #1404: Run läuft bis response.done/terminaler Fehler
    setStateBoth('thinking');
  }, [setStateBoth]);

  // ── Mic-Frame-Verarbeitung (VAD + Echo-Guard + Barge-in) ──
  const onMicFrame = useCallback((frame) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const speaking = stateRef.current === 'speaking';
    // #1388 F2: VAD bewertet das UNGEDÄMPFTE Mic-Signal. Der Echo-Guard (×0.15)
    // dämpft nur noch das GEPUFFERTE Audio während die AI spricht — er liegt
    // nicht mehr vor der VAD-Entscheidung (Barge-in war sonst erst bei RMS ≈0,15
    // erreichbar). Gegen Selbst-Trigger durchs AI-Audio gilt während speaking
    // die separate, höhere Schwelle BARGE_IN_RMS.
    let sum = 0;
    for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
    const rms = Math.sqrt(sum / frame.length);
    micRmsRef.current = micRmsRef.current * 0.8 + rms * 0.2;
    let data = frame;
    if (speaking) {
      data = new Float32Array(frame.length);
      for (let i = 0; i < frame.length; i++) data[i] = frame[i] * ECHO_GUARD_FACTOR;
    }

    const frameMs = (data.length / ctx.sampleRate) * 1000;
    const vad = vadRef.current;

    if (!vad.capturing) {
      // Pre-Roll-Ringpuffer pflegen
      preRollRef.current.push(data);
      let ms = 0;
      for (const c of preRollRef.current) ms += (c.length / ctx.sampleRate) * 1000;
      while (ms > PRE_ROLL_MS && preRollRef.current.length > 1) {
        ms -= (preRollRef.current[0].length / ctx.sampleRate) * 1000;
        preRollRef.current.shift();
      }
      if (rms > (speaking ? BARGE_IN_RMS : VAD_START_RMS)) {
        vad.speechMs += frameMs;
        vad.gapMs = 0;
        if (vad.speechMs >= (speaking ? BARGE_IN_MS : VAD_START_MS)) {
          const ws = wsRef.current;
          if (!ws || ws.readyState !== 1) return;
          // Barge-in (#1388, AK1): reinreden während speaking ODER thinking →
          // Abort ans Gateway (bricht laufende Pipeline ab) + Playback leeren.
          if (stateRef.current === 'speaking' || stateRef.current === 'thinking') {
            ws.send(JSON.stringify({ type: 'abort' }));
            runActiveRef.current = false; // #1404: Abort beendet den Run serverseitig
            stopPlayback();
            setStateBoth('listening');
          }
          vad.capturing = true;
          vad.gapMs = 0;
          vad.silenceMs = 0;
          vad.utterMs = 0;
          chunksRef.current = [...preRollRef.current];
          preRollRef.current = [];
          setVisual(null); // #1399 AK3: neue Frage blendet das Visual aus/um
          ws.send(JSON.stringify({ type: 'speech.started', mime: 'audio/wav' }));
        }
      } else {
        // #1398 Hangover statt Hard-Reset: kurze Dips (Plosive, Wortgrenzen)
        // löschen die akkumulierte Sprechzeit nicht mehr sofort.
        vad.gapMs += frameMs;
        if (vad.gapMs > VAD_HANGOVER_MS) vad.speechMs = 0;
      }
      return;
    }

    // Capturing
    chunksRef.current.push(data);
    vad.utterMs += frameMs;
    if (rms < VAD_STOP_RMS) vad.silenceMs += frameMs; else vad.silenceMs = 0;
    if (vad.silenceMs >= VAD_STOP_MS || vad.utterMs >= MAX_UTTERANCE_MS) {
      vad.capturing = false;
      vad.speechMs = 0;
      vad.gapMs = 0;
      flushUtterance();
    }
  }, [flushUtterance, setStateBoth, stopPlayback]);

  // ── Amplitude-Loop für den Orb (Mic beim Listening, Output beim Speaking) ──
  useEffect(() => {
    const tick = () => {
      let amp = 0;
      if (stateRef.current === 'speaking' && analyserRef.current) {
        const buf = new Uint8Array(analyserRef.current.fftSize);
        analyserRef.current.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        amp = Math.sqrt(sum / buf.length);
      } else if (stateRef.current === 'listening') {
        amp = micRmsRef.current;
      }
      setAmplitude(Math.min(1, amp * 6));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // #1390 — Mic/WS/Timer freigeben, Playback + AudioContext aber AM LEBEN lassen:
  // Drain-Phase nach session.ended (Verabschiedungs-Audio spielt noch).
  const releaseCapture = useCallback(() => {
    clearInterval(timerRef.current);
    if (workletRef.current) { try { workletRef.current.port.onmessage = null; workletRef.current.disconnect(); } catch { /* noop */ } workletRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      try { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'session.end' })); } catch { /* noop */ }
      try { ws.close(); } catch { /* noop */ }
    }
    vadRef.current = { capturing: false, speechMs: 0, gapMs: 0, silenceMs: 0, utterMs: 0 };
    runActiveRef.current = false;
    preRollRef.current = [];
    chunksRef.current = [];
    micRmsRef.current = 0;
  }, []);

  const cleanup = useCallback(() => {
    stopPlayback();
    releaseCapture();
    if (ctxRef.current) { ctxRef.current.close().catch(() => {}); ctxRef.current = null; }
    analyserRef.current = null;
    drainingRef.current = false;
    standbyOptsRef.current = null;
    timeoutSentRef.current = false;
    setStateBoth('idle');
  }, [releaseCapture, setStateBoth, stopPlayback]);
  useEffect(() => { cleanupRef.current = cleanup; }, [cleanup]);

  const stop = useCallback(() => { cleanup(); }, [cleanup]);

  /**
   * @param {Object} [opts] - #1390: { standby: true } aktiviert die Idle-Timeout-
   *   Überwachung (Auto-Standby, Kiosk). Timeout-Wert kommt vom Server
   *   (session.started limits.standby_timeout_seconds). Ohne opts: Verhalten
   *   exakt wie bisher (kein Timer, AK5).
   */
  const start = useCallback(async (opts = {}) => {
    if (stateRef.current !== 'idle') return;
    setError(null);
    setEndReason(null);
    setTurns([]);
    setElapsed(0);
    setPendingAction(null);
    setActionResult(null);
    setVisual(null);
    standbyOptsRef.current = opts.standby ? { idleTimeoutSeconds: null } : null;
    timeoutSentRef.current = false;
    drainingRef.current = false;
    setStateBoth('connecting');

    // 1) Mic-Permission (AK1) — vor dem WS, damit der Flow klar scheitert.
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      setError({ code: 'mic_denied' });
      setStateBoth('idle');
      return;
    }
    streamRef.current = stream;

    // 2) AudioContext + Worklet (PCM-Capture)
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const blobUrl = URL.createObjectURL(new Blob([WORKLET_CODE], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);
      const src = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'weship-voice-capture');
      node.port.onmessage = (e) => onMicFrame(e.data);
      src.connect(node);
      workletRef.current = node;
    } catch {
      setError({ code: 'audio_unsupported' });
      cleanup();
      return;
    }

    // 3) WebSocket (Cookie-Auth same-origin)
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/ws/voice`);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      switch (msg.type) {
        case 'session.started':
          setMaxSeconds(msg.limits?.max_session_seconds || null);
          // #1390: Auto-Standby-Timeout — Server-Wert übernehmen (nur Standby-Modus).
          if (standbyOptsRef.current) {
            standbyOptsRef.current.idleTimeoutSeconds = Number(msg.limits?.standby_timeout_seconds) || 60;
          }
          setStateBoth('listening');
          clearInterval(timerRef.current);
          timerRef.current = setInterval(() => {
            setElapsed((s) => s + 1);
            // #1390: X s ununterbrochene Stille in ACTIVE (listening, keine
            // laufende Aufnahme) → standby.timeout ans Gateway (1×). Antwort:
            // kurze TTS-Verabschiedung + session.ended('standby_timeout').
            const so = standbyOptsRef.current;
            if (
              so && so.idleTimeoutSeconds && !timeoutSentRef.current &&
              stateRef.current === 'listening' && !vadRef.current.capturing &&
              Date.now() - lastActivityRef.current >= so.idleTimeoutSeconds * 1000
            ) {
              timeoutSentRef.current = true;
              try { wsRef.current?.send(JSON.stringify({ type: 'standby.timeout' })); } catch { /* noop */ }
            }
          }, 1000);
          break;
        case 'thinking':
          setStateBoth('thinking');
          break;
        case 'response.done':
          runActiveRef.current = false; // #1404: Run terminal — Playback-Ende darf wieder auf listening
          setTurns((prev) => [
            ...prev,
            ...(msg.transcript ? [{ role: 'user', text: msg.transcript }] : []),
            ...(msg.text ? [{ role: 'assistant', text: msg.text }] : []),
          ]);
          // Audio folgt separat; bis dahin wieder hören.
          if (stateRef.current === 'thinking') setStateBoth('listening');
          break;
        case 'audio':
          // Stale Audio nach Barge-in/Abort nicht abspielen (User spricht gerade).
          if (vadRef.current.capturing) break;
          if (msg.encoding === 'base64' && msg.data) {
            queueRef.current.push(msg.data);
            playNext();
          }
          break;
        case 'visual.show':
          // #1399 — Visual einblenden/umblenden (read-only Anzeige).
          if (msg.visual && msg.visual.typ) setVisual(msg.visual);
          break;
        case 'visual.hide':
          setVisual(null);
          break;
        case 'action.pending':
          // #1387 — Vorschlag anzeigen, bis das gesprochene Ja/Nein entschieden hat.
          setActionResult(null);
          setPendingAction({ recipient: msg.recipient, message: msg.message });
          break;
        case 'action.result':
          setPendingAction(null);
          setActionResult({ status: msg.status || 'failed' });
          break;
        case 'error':
          // #1404: 'busy' weist nur eine NEUE Äußerung ab — der laufende Run
          // lebt weiter (in 'thinking' bleiben, response.done kommt noch);
          // jeder andere Fehler beendet den Run terminal.
          if (msg.code !== 'busy') runActiveRef.current = false;
          setError({ code: msg.code || 'unknown' });
          if (stateRef.current === 'thinking' && !runActiveRef.current) setStateBoth('listening');
          break;
        case 'session.ended':
          setEndReason(msg.reason || 'ended');
          // #1390: Verabschiedungs-Audio (farewell/standby_timeout) zu Ende
          // spielen lassen — Mic/WS sofort zu, voller Cleanup nach dem Drain.
          if (sourceRef.current || decodingRef.current || queueRef.current.length) {
            drainingRef.current = true;
            releaseCapture();
            playNext(); // falls Queue gefüllt, aber noch nichts spielt
          } else {
            cleanup();
          }
          break;
        default:
          break;
      }
    };
    ws.onerror = () => {
      if (stateRef.current === 'connecting') { setError({ code: 'connection' }); cleanup(); }
    };
    ws.onclose = () => {
      if (drainingRef.current) return; // #1390: Drain läuft — cleanup kommt nach dem letzten Audio
      if (stateRef.current !== 'idle') {
        if (stateRef.current === 'connecting') setError({ code: 'connection' });
        cleanup();
      }
    };
  }, [cleanup, onMicFrame, playNext, releaseCapture, setStateBoth]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { state, amplitude, elapsed, maxSeconds, turns, error, endReason, pendingAction, actionResult, visual, start, stop };
}
